import {
  LayoutGrid,
  Gift,
  FolderOpen,
  Globe,
  Plug,
  Users,
  CreditCard,
  UserRound,
  LifeBuoy,
  HelpCircle,
  LayoutDashboard,
  Code2,
  Megaphone,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import type { PlatformFeature } from "@/hooks/usePlanFeatures";
import { ShieldUser } from "./icons";

export interface ShellNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  // Path prefixes that should also highlight this item (e.g. every /billing/* page)
  match?: string[];
  gate?: PlatformFeature;
  teamOnly?: boolean;
}

export interface ShellNavGroup {
  label: string;
  items: ShellNavItem[];
}

export const SHELL_NAV: ShellNavGroup[] = [
  {
    label: "Workspace",
    items: [
      { to: "/~", label: "Overview", icon: LayoutGrid },
      { to: "/projects/all", label: "Projects", icon: FolderOpen },
      { to: "/published", label: "Published apps", icon: Globe, gate: "published_apps" },
      { to: "/expert", label: "Expert help", icon: ShieldUser, match: ["/expert"] },
      { to: "/connectors", label: "Connectors", icon: Plug },
    ],
  },
  {
    label: "Manage",
    items: [
      { to: "/workspace/settings", label: "Team & workspace", icon: Users },
      { to: "/billing/manage", label: "Billing & plans", icon: CreditCard, match: ["/billing"] },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/profile", label: "My profile", icon: UserRound },
      { to: "/security", label: "Security", icon: ShieldCheck },
    ],
  },
];

export const SHELL_FOOTER_NAV: ShellNavItem[] = [
  { to: "/referrals", label: "Share Joylo", icon: Gift },
  { to: "/my-tickets", label: "Support requests", icon: LifeBuoy },
  { to: "/help", label: "Help center", icon: HelpCircle },
];

export const ROLE_NAV = {
  admin: { to: "/admin", label: "Admin console", icon: LayoutDashboard, match: ["/admin", "/analytics"] },
  developer: { to: "/developer/dashboard", label: "Developer dashboard", icon: Code2 },
  influencer: { to: "/influencer", label: "Influencer board", icon: Megaphone },
} satisfies Record<string, ShellNavItem>;

// Titles for the topbar trail; the first matching prefix wins, so keep specific paths first
const PAGE_TITLES: [string, string][] = [
  ["/~", "Overview"],
  ["/projects/all", "Projects"],
  ["/published", "Published apps"],
  ["/connectors", "Connectors"],
  ["/expert/request", "Request expert help"],
  ["/expert", "Expert help"],
  ["/workspace/settings", "Team & workspace"],
  ["/billing/plans", "Compare plans"],
  ["/billing/payment", "Payment"],
  ["/billing/success", "Payment complete"],
  ["/billing", "Billing & plans"],
  ["/profile", "My profile"],
  ["/security", "Security"],
  ["/universal-ai-key", "Universal AI Key"],
  ["/referrals", "Share Joylo"],
  ["/my-tickets", "Support requests"],
  ["/influencer", "Influencer board"],
  ["/developer/dashboard", "Developer dashboard"],
  ["/analytics", "Usage analytics"],
  ["/admin", "Admin console"],
  ["/help", "Help center"],
  ["/projects", "Expert engagement"],
];

export const pageTitleFor = (pathname: string) =>
  PAGE_TITLES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.[1] ?? "";

// Account-level pages read "Your account" in the topbar instead of the workspace switch
export const ACCOUNT_SCOPED_PATHS = ["/profile", "/security", "/universal-ai-key", "/referrals", "/my-tickets", "/influencer"];

export const isNavItemActive = (item: ShellNavItem, pathname: string) =>
  pathname === item.to || (item.match ?? []).some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
